import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Trash2 } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const SKIN_TYPES = [
  { value: "normal", label: "Normal" }, { value: "seca", label: "Seca" }, { value: "oleosa", label: "Oleosa" },
  { value: "mista", label: "Mista" }, { value: "sensivel", label: "Sensível" }, { value: "acneica", label: "Acneica" },
];

const emptyForm = { first_name: "", last_name: "", email: "", whatsapp: "", skin_type: "normal", newsletter_opt_in: false, notes: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [skinFilter, setSkinFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (skinFilter !== "all") params.set("skin_type", skinFilter);
    api.get(`/admin/clients?${params.toString()}`).then((r) => setClients(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, skinFilter]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/clients", form);
      toast.success("Cliente cadastrada!");
      setOpen(false); setForm(emptyForm); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    await api.delete(`/admin/clients/${id}`);
    toast.success("Cliente removida");
    load();
  };

  return (
    <div data-testid="admin-clients-page">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-serif text-[#9B6179]">Clientes</h1>
        <Button onClick={() => setOpen(true)} data-testid="new-client-button" className="bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors"><Plus className="w-4 h-4 mr-1" /> Nova Cliente</Button>
      </div>

      <div className="flex gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F3F55]" />
          <Input placeholder="Buscar por nome, e-mail, whatsapp..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" data-testid="clients-search-input" />
        </div>
        <Select value={skinFilter} onValueChange={setSkinFilter}>
          <SelectTrigger className="w-48" data-testid="clients-skin-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos de pele</SelectItem>
            {SKIN_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="bg-white rounded-[2px] shadow-sm border border-[#9B6179]/5 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F7F0E8] text-[#6F3F55] text-xs uppercase">
            <tr><th className="text-left px-4 py-3">Nome</th><th className="text-left px-4 py-3">E-mail</th><th className="text-left px-4 py-3">WhatsApp</th><th className="text-left px-4 py-3">Pele</th><th className="px-4 py-3"></th></tr>
          </thead>
          <tbody>
            {clients.map((c) => (
              <tr key={c.id} className="border-t border-[#9B6179]/5 hover:bg-[#FAF7F5]" data-testid={`client-row-${c.id}`}>
                <td className="px-4 py-3"><Link to={`/admin/clientes/${c.id}`} className="text-[#9B6179] font-medium hover:underline" data-testid={`client-link-${c.id}`}>{c.first_name} {c.last_name}</Link></td>
                <td className="px-4 py-3 text-[#594A50]">{c.email}</td>
                <td className="px-4 py-3 text-[#594A50]">{c.whatsapp}</td>
                <td className="px-4 py-3 text-[#594A50] capitalize">{c.skin_type}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => remove(c.id)} data-testid={`delete-client-${c.id}`} className="text-[#6F3F55] hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {clients.length === 0 && <tr><td colSpan={5} className="text-center py-8 text-[#6F3F55]">Nenhuma cliente encontrada.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="new-client-modal">
          <form onSubmit={submit} className="space-y-3">
            <p className="font-serif text-lg text-[#9B6179]">Nova Cliente</p>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} data-testid="client-form-firstname" /></div>
              <div><Label>Sobrenome</Label><Input required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} data-testid="client-form-lastname" /></div>
            </div>
            <div><Label>E-mail</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="client-form-email" /></div>
            <div><Label>WhatsApp</Label><Input required value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="+33..." data-testid="client-form-whatsapp" /></div>
            <div>
              <Label>Tipo de pele</Label>
              <Select value={form.skin_type} onValueChange={(v) => setForm({ ...form, skin_type: v })}>
                <SelectTrigger data-testid="client-form-skintype"><SelectValue /></SelectTrigger>
                <SelectContent>{SKIN_TYPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox checked={form.newsletter_opt_in} onCheckedChange={(v) => setForm({ ...form, newsletter_opt_in: v })} data-testid="client-form-newsletter" />
              <Label>Aceita receber newsletter</Label>
            </div>
            <Button disabled={saving} type="submit" className="w-full bg-[#090708] text-[#E8C47A] rounded-[2px] hover:bg-[#6F3F55] transition-colors" data-testid="client-form-submit">Salvar</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
